// ============================================================================
// AUDIT FIRM MASTER SCREEN — FSA Signature Workflow
// ============================================================================
// Lists audit firms from fsa.tbl_audit_firm. Standard CRUD with
// SyutGrid + filter panel + Add/Edit form modal + Delete ConfirmDialog.
// Follows the FSA Engagements list pattern (FsaEngagementListScreen):
// list ↔ grid view toggle, ColumnSelector, filter count badge,
// MinimizedModalStrip, allowedActions gating.
// Sister screens follow the same shape:
//   - FsaAuditPartnerMasterScreen (drilled-in from a firm row)
//   - Director master tab on Client master
//   - FsaEngagementSignaturePanelScreen (per-engagement)

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
  ColumnSelector,
  type ColumnDefinition,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useFsaAuditFirms } from 'hooks/api/fsa/useFsaSignature';
import { fsaAuditFirmService } from 'api/services/fsa/fsaSignatureService';
import type { AuditFirm, AuditFirmSearchRequest } from 'api/types/fsa/signature.types';
import AuditFirmFormModal from 'components/modals/modules/fsa/AuditFirmFormModal';

type ViewMode = 'list' | 'grid';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ACTIVE', label: 'Active only' },
  { value: 'INACTIVE', label: 'Inactive only' },
];

// Column definitions powering the ColumnSelector (toolbar gear menu).
const columnDefinitions: ColumnDefinition[] = [
  { id: 'firmName',     label: 'Firm Name', field: 'firmName',               required: true,  defaultVisible: true, sortable: true },
  { id: 'frn',          label: 'FRN',       field: 'firmRegistrationNumber', required: false, defaultVisible: true, sortable: true },
  { id: 'partnerCount', label: 'Partners',  field: 'partnerCount',           required: false, defaultVisible: true, sortable: false },
  { id: 'email',        label: 'Email',     field: 'email',                  required: false, defaultVisible: true, sortable: false },
  { id: 'phone',        label: 'Phone',     field: 'phone',                  required: false, defaultVisible: true, sortable: false },
  { id: 'status',       label: 'Status',    field: 'statusName',             required: false, defaultVisible: true, sortable: true },
  { id: 'createdAt',    label: 'Created',   field: 'createdAt',              required: false, defaultVisible: true, sortable: true },
];

const COLUMN_PREF_KEY = 'fsa-audit-firm-columns';

const formatDate = (iso?: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '-'; }
};

interface FsaAuditFirmMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const FsaAuditFirmMasterScreen: React.FC<FsaAuditFirmMasterScreenProps> = ({
  moduleIcon,
  tabId,
  moduleName,
  allowedActions = [],
}) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // Server-side pagination + filter state
  const [queryParams, setQueryParams] = useState<AuditFirmSearchRequest>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useFsaAuditFirms(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [firmNameFilter, setFirmNameFilter] = useState('');
  const [frnFilter, setFrnFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Column visibility — persisted to localStorage (per the enterprise list pattern).
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id)
  );

  useEffect(() => {
    const saved = localStorage.getItem(COLUMN_PREF_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setVisibleColumns(parsed);
      } catch { /* ignore */ }
    }
  }, []);

  const handleApplyColumns = useCallback((cols: string[]) => {
    setVisibleColumns(cols);
    localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(cols));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaults = columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id);
    setVisibleColumns(defaults);
    localStorage.removeItem(COLUMN_PREF_KEY);
  }, []);

  // Server-side search debounce.
  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handlePageChange = useCallback((idx: number) => {
    setQueryParams(prev => ({ ...prev, page: idx + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams(prev => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.firmName) count++;
    if (queryParams.firmRegistrationNumber) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [queryParams.firmName, queryParams.firmRegistrationNumber, queryParams.statusCode]);

  const applyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      firmName:               firmNameFilter || undefined,
      firmRegistrationNumber: frnFilter || undefined,
      statusCode:             statusFilter || undefined,
      includeInactive:        statusFilter === 'INACTIVE',
      page:                   1,
    }));
    setFilterOpen(false);
  }, [firmNameFilter, frnFilter, statusFilter]);

  const clearFilters = useCallback(() => {
    setFirmNameFilter('');
    setFrnFilter('');
    setStatusFilter('');
    setQueryParams(prev => ({
      ...prev,
      firmName: undefined, firmRegistrationNumber: undefined, statusCode: undefined, includeInactive: undefined, page: 1,
    }));
  }, []);

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedFirm, setSelectedFirm] = useState<AuditFirm | null>(null);

  // Minimize support — multi-instance Add + restore of minimized sessions
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit', instanceId?: string, recordId?: string) => {
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordId) {
        const rec = data.find((r) => String(r.id) === recordId);
        if (rec) setSelectedFirm(rec);
      }
      setFormMode(restoredMode);
      setFormModalOpen(true);
    },
    [data]
  );

  // Delete state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Columns
  const columns: SyutGridColumn<AuditFirm>[] = useMemo(() => [
    { id: 'firmName', header: 'Firm Name', accessorKey: 'firmName', enableSorting: true, size: 280 },
    { id: 'frn', header: 'FRN', accessorKey: 'firmRegistrationNumber', enableSorting: true, size: 180 },
    { id: 'partnerCount', header: 'Partners',
      accessorFn: (row: AuditFirm) => row.partnerCount,
      cell: ({ row }: { row: { original: AuditFirm } }) => <span style={{ display: 'inline-block', minWidth: 28, textAlign: 'center', padding: '0.125rem 0.375rem', borderRadius: 4, background: 'var(--bg-secondary)', fontSize: '0.8125rem' }}>{row.original.partnerCount}</span>,
      size: 80, enableSorting: false },
    { id: 'email', header: 'Email', accessorKey: 'email', enableSorting: false, size: 200 },
    { id: 'phone', header: 'Phone', accessorKey: 'phone', enableSorting: false, size: 120 },
    { id: 'status', header: 'Status', accessorKey: 'statusName', enableSorting: true, size: 100,
      cell: ({ row }: { row: { original: AuditFirm } }) => {
        const isActive = row.original.statusCode === 'ACTIVE';
        return (
          <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
            {row.original.statusName || (isActive ? 'Active' : 'Inactive')}
          </span>
        );
      } },
    { id: 'createdAt', header: 'Created',
      accessorFn: (row: AuditFirm) => row.createdAt,
      cell: ({ row }: { row: { original: AuditFirm } }) => <span>{formatDate(row.original.createdAt)}</span>,
      size: 110, enableSorting: true },
  ], []);

  const visibleTableColumns = useMemo<SyutGridColumn<AuditFirm>[]>(
    () => columns.filter((col) => visibleColumns.includes(col.id as string)),
    [columns, visibleColumns]
  );

  // Row actions — if the same record is already minimized, restore it
  const handleEdit = useCallback((row: AuditFirm) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${String(row.id)}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setSelectedFirm(row);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleDeleteClick = useCallback((row: AuditFirm) => {
    setSelectedFirm(row);
    setDeleteModalOpen(true);
  }, []);

  const getRowActions = useCallback((row: AuditFirm): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (allowedActions.includes('edit')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={16} />, onClick: () => handleEdit(row) });
    }
    if (allowedActions.includes('delete')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => handleDeleteClick(row), variant: 'danger' });
    }
    return items;
  }, [allowedActions, handleEdit, handleDeleteClick]);

  // Action buttons — each Add opens a fresh instance (multi-instance minimize)
  const handleAdd = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedFirm(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, []);

  const actionButtons: ActionButton[] = useMemo(() => [
    ...(allowedActions.includes('add') || allowedActions.includes('create') ? [{
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'Add',
      tooltip: 'Add audit firm',
      onClick: handleAdd,
      variant: 'icon' as const,
    }] : []),
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      tooltip: 'Refresh',
      onClick: () => refetch(),
      variant: 'icon' as const,
    },
  ], [allowedActions, handleAdd, refetch]);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedFirm) return;
    setIsDeleting(true);
    try {
      await fsaAuditFirmService.remove(selectedFirm.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success(`"${selectedFirm.firmName}" deleted successfully.`);
      setSelectedFirm(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      notificationService.toast.error(err?.response?.data?.message ?? 'Delete failed.', 6000);
    }
  }, [selectedFirm, refetch]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Audit Firms"
        description="Firm-wide audit firm master. Used by FSA Signature Workflow to populate the auditor side of signed Financial Statements."
        icon={
          moduleIcon && (IconSystem as any)[moduleIcon]
            ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
            : <IconSystem.users size={18} />
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search firms…',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={visibleTableColumns}
                enableRowSelection
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                enableSorting
                rowActions={getRowActions}
                emptyMessage={loading ? 'Loading audit firms…' : 'No audit firms found'}
              />
            )}

            {viewMode === 'grid' && (
              <div
                className="syut-grid-component"
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '1.5rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {data.length === 0 && (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                      {loading ? 'Loading audit firms…' : 'No audit firms found'}
                    </div>
                  )}
                  {data.map((item) => {
                    const isActive = item.statusCode === 'ACTIVE';
                    return (
                      <div
                        key={String(item.id)}
                        style={{
                          background: 'var(--bg-primary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          padding: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                              {item.firmRegistrationNumber || '-'}
                            </div>
                            <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                              {item.firmName || '-'}
                            </div>
                          </div>
                          <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
                            {item.statusName || (isActive ? 'Active' : 'Inactive')}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                          Partners: {item.partnerCount ?? 0}
                        </div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                          {item.email || '-'}{item.phone ? ` · ${item.phone}` : ''}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          Created: {formatDate(item.createdAt)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Firm Name</label>
                <input type="text" className="form-input" value={firmNameFilter}
                  onChange={(e) => setFirmNameFilter(e.target.value)}
                  placeholder="Filter by firm name…"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>FRN</label>
                <input type="text" className="form-input" value={frnFilter}
                  onChange={(e) => setFrnFilter(e.target.value)}
                  placeholder="Filter by registration number…"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={STATUS_FILTER_OPTIONS}
                  value={statusFilter}
                  onChange={(val) => setStatusFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={`Delete audit firm "${selectedFirm?.firmName}"? Existing engagement signature panels that reference this firm will keep their snapshot data, but new engagements won't be able to select it.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting…"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
      />

      <AuditFirmFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedFirm}
        onSaved={refetch}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />
    </div>
  );
};

export default FsaAuditFirmMasterScreen;
